import { useCallback, useState } from "react";

import { Popover } from "antd";
import { TooltipPlacement } from "antd/lib/tooltip";
import { clsx } from "clsx";

import sharedStyles from "../../SharedInputStyling.module.scss";
import { LinkDetails, LinkEdit } from "../LinkEdit/LinkEdit";

type EmailLinkPopoverManagerProps = React.PropsWithChildren<{
    expanded: boolean;
    className?: string;
    onLinkEdit: (values: LinkDetails) => void;
    onLinkRemove: () => void;
}>;

// TODO instead of this harcoded height
// we should read the height of the Popover's contents
const HARCODED_MODAL_HEIGHT = 296;

function getLinkElement(element: HTMLElement) {
    if (element.tagName?.toLowerCase() === "a") {
        return element;
    }

    if (element.parentElement?.tagName?.toLowerCase() === "a") {
        return element.parentElement;
    }

    return null;
}

function getEditorElement(element: HTMLElement) {
    let result: HTMLElement | null = element;
    while (result && !result.hasAttribute("contenteditable")) {
        result = result.parentElement;
    }
    return result?.parentElement;
}

export function EmailLinkPopoverManager({
    children,
    expanded = false,
    onLinkEdit,
    onLinkRemove,
    className,
}: EmailLinkPopoverManagerProps) {
    const [editingLink, setEditingLink] = useState<HTMLAnchorElement | undefined>();
    const [popupOffset, setPopupOffset] = useState<number[]>([0, 0]);
    const [placement, setPlacement] = useState<TooltipPlacement>("topLeft");

    const handleClickOutsideLinkEdit = useCallback(() => {
        setEditingLink(undefined);
    }, [setEditingLink]);

    const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const targetElement = getLinkElement(event.target as HTMLElement);
        // if we clicked one of our links
        if (targetElement) {
            event.preventDefault();

            // we use both the anchor element's position
            // and the editor's position because the editor has an overflow (is scrollable)
            // and that affects the values for the anchor's position
            const editorElem = getEditorElement(targetElement) as HTMLElement;
            const { top: anchorTop, height: anchorHeight, left: anchorLeft } = targetElement.getBoundingClientRect();
            const { top: editorTop, height: editorHeight, left: editorLeft } = editorElem.getBoundingClientRect();

            // determine if the top position for the anchor
            // is so high up that it would cause the top part of the popover's contents to overflow
            const isOverflowingOnTop = anchorTop < HARCODED_MODAL_HEIGHT;

            // whenever the popover is overflowing on the top, we move it to the bottom
            // but in doing so, the origin point on the popover also changes from top to bottom
            // normally we want to show the popover on the top left, and the origin would be at the top of the element
            // so we move "down" on the Y axis (negative)
            // when it overflows, we move the origin at the bottom and we move "up" on the Y axis (positive)
            const targetY = isOverflowingOnTop ? editorHeight - anchorHeight : -(anchorTop - editorTop);

            setPlacement(isOverflowingOnTop ? "bottomLeft" : "topLeft");
            setPopupOffset([editorLeft - anchorLeft, targetY]);
            setEditingLink(targetElement as HTMLAnchorElement);
        }
    };

    const handleLinkEdited = useCallback(
        (values: LinkDetails) => {
            if (editingLink) {
                onLinkEdit(values);
                setEditingLink(undefined);
            }
        },
        [editingLink, onLinkEdit]
    );

    const handleLinkRemoved = useCallback(() => {
        if (editingLink) {
            onLinkRemove();
            setEditingLink(undefined);
        }
    }, [editingLink, onLinkRemove]);

    return (
        <div className={clsx(sharedStyles.lexicalEditorContainer, { [sharedStyles.expanded]: expanded })}>
            <Popover
                content={
                    <LinkEdit
                        link={editingLink?.href as string}
                        displayText={editingLink?.innerText as string}
                        isNew={false}
                        onClickOutside={handleClickOutsideLinkEdit}
                        onConfirm={handleLinkEdited}
                        onRemove={handleLinkRemoved}
                    />
                }
                open={Boolean(editingLink)}
                placement={placement}
                destroyTooltipOnHide={{ keepParent: false }}
                align={{ targetOffset: popupOffset }} // we position the tooltip on top of the clicked link
                autoAdjustOverflow={false}
                arrowPointAtCenter={false}
            >
                <div className={clsx(sharedStyles.lexicalEditorScrollContainer, className)} onClick={handleClick}>
                    {children}
                </div>
            </Popover>
        </div>
    );
}
